import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * ExperienceStateBadge — lifecycle chip for an AI Experience: Draft,
 * In Progress, Ready, Shared, Archived. Completes the badge family next
 * to `<ExperienceTypeBadge>` (what it is) and `<VisibilityBadge>` (who can
 * see it) — this one says where it is in the publish pipeline.
 *
 * Ported from Creator Studio v2
 * (`src/components/experience/ExperienceStateBadge.tsx`) and re-themed: the
 * app's `--color-*` palette with hex fallbacks is replaced with this
 * library's semantic tokens (`--info`, `--accent`, `--success`, `--muted`),
 * and the state → label/tone lookup is exported as
 * `getExperienceStateMeta` following the `getExperienceTypeMeta` /
 * `getVisibilityMeta` convention so list rows, filters, and toasts can reuse
 * the canonical labels without rendering the badge.
 *
 * Unknown states render with a neutral tone and a humanised label
 * (`"in_review"` → "In Review") so consumers never have to guard the prop.
 *
 * @example
 *   <ExperienceStateBadge state="published" />
 *   <ExperienceStateBadge state="in_progress" size="sm" />
 */

export type ExperienceState =
  | "draft"
  | "in_progress"
  | "ready_to_publish"
  | "published"
  | "archived"
  | string;

export type ExperienceStateTone = "neutral" | "info" | "accent" | "success" | "faded" | "error";

interface ExperienceStateMeta {
  label: string;
  tone: ExperienceStateTone;
}

const STATE_META: Record<string, ExperienceStateMeta> = {
  draft: { label: "Draft", tone: "neutral" },
  in_progress: { label: "In Progress", tone: "info" },
  // `generating` is the unified#103 `/items/` name for in-flight work; it
  // shares `in_progress`'s info tone so the lifecycle vocabulary has one source.
  generating: { label: "Generating", tone: "info" },
  ready_to_publish: { label: "Ready", tone: "accent" },
  // `ready` is the #103 settled-and-generated state (file Indexed, experience
  // generated-not-yet-published) — success-toned, distinct from the
  // `ready_to_publish` CTA state above which is accent-toned.
  ready: { label: "Ready", tone: "success" },
  published: { label: "Shared", tone: "success" },
  failed: { label: "Failed", tone: "error" },
  archived: { label: "Archived", tone: "faded" },
};

/** "in_review" → "In Review" — a readable fallback for unknown states. */
const humanise = (state: string): string =>
  state
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Look up the canonical label + tone for a state. Unknown states fall back
 * to a neutral tone with a humanised label, so consumers never need to
 * guard against missing keys.
 */
export const getExperienceStateMeta = (state: ExperienceState): ExperienceStateMeta =>
  STATE_META[(state ?? "").toLowerCase()] ?? {
    label: humanise(state || "Unknown"),
    tone: "neutral",
  };

const experienceStateBadgeVariants = cva(
  "inline-flex shrink-0 items-center whitespace-nowrap rounded-full border font-medium",
  {
    variants: {
      tone: {
        neutral: "border-transparent bg-muted text-muted-foreground",
        info: "border-info/20 bg-info/10 text-info",
        accent: "border-accent/20 bg-accent/10 text-accent",
        success: "border-success/20 bg-success/10 text-success",
        faded: "border-transparent bg-muted/60 text-muted-foreground/60",
        error: "border-destructive/20 bg-destructive/10 text-destructive",
      },
      size: {
        sm: "px-2 py-0.5 text-[11px]",
        md: "px-2.5 py-1 text-xs",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  },
);

export interface ExperienceStateBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    Pick<VariantProps<typeof experienceStateBadgeVariants>, "size"> {
  /** Lifecycle state. Unknown values render neutral with a humanised label. */
  state: ExperienceState;
  /** Override the auto-selected tone. */
  tone?: ExperienceStateTone;
}

export const ExperienceStateBadge = React.forwardRef<HTMLSpanElement, ExperienceStateBadgeProps>(
  ({ state, tone, size = "md", className, ...rest }, ref) => {
    const meta = getExperienceStateMeta(state);
    return (
      <span
        ref={ref}
        className={cn(experienceStateBadgeVariants({ tone: tone ?? meta.tone, size }), className)}
        {...rest}
      >
        {meta.label}
      </span>
    );
  },
);
ExperienceStateBadge.displayName = "ExperienceStateBadge";
